import type { PostEntryInput, PostingLineInput } from '@taler/api-client'
import {
  addMoney,
  type CurrencyCode,
  type LocalDate,
  type Money,
  negateMoney,
  zeroMoney,
} from '@taler/domain'
import type { Target } from './targets.ts'

export type FlowKind = 'EXPENSE' | 'INCOME' | 'TRANSFER'

/** One target on the counter side of the row's entry - a category, a contact, or (for a
 * transfer) the destination account. A row starts with one and the captain's split
 * requirement adds more, each carrying its own amount and memo. A split's amount is always
 * a magnitude (never negative - the row's own kind, not the split, decides sign on the
 * wire), enforced by the `AmountInput` it's typed into rather than re-checked here. */
export interface SplitDraft {
  readonly id: string
  targetId: string | undefined
  amount: Money | null
  memo: string
  /** The last category-class target this split held, kept even after the split is
   * pointed at a contact - carried onto the wire as `splitFromAccountId`, recording
   * which expense account a contact's share was carved out of (feature-contacts.md §2.5). */
  lastCategoryId: string | undefined
  /** Set when `targetId` was picked from another ledger's group - a fixture account id can
   * coincide with a home one, so this is the only trustworthy source of its real ledger. */
  targetLedgerId: string | undefined
}

/** One grid row: a whole entry, keyboard-committed as a unit. `accountId` is the money
 * account the amount actually moves through (the "from" side of a transfer too). */
export interface RowDraft {
  readonly id: string
  date: LocalDate
  accountId: string | undefined
  /** Set when `accountId` was picked from another ledger's group - see `SplitDraft.targetLedgerId`. */
  accountLedgerId: string | undefined
  memo: string
  /** Who was paid or who paid - the free text `PostEntryRequest.payee` carries; a
   * merchant is resolved (or created) from it server-side, never sent by id. */
  payee: string | undefined
  tags: string[]
  splits: SplitDraft[]
}

export function createSplit(): SplitDraft {
  return {
    id: crypto.randomUUID(),
    targetId: undefined,
    amount: null,
    memo: '',
    lastCategoryId: undefined,
    targetLedgerId: undefined,
  }
}

/** Sets a split's target, remembering it as {@link SplitDraft.lastCategoryId} whenever it
 * resolves to an account - the one place this bookkeeping happens, so every caller that
 * changes a split's target (not just direct field assignment) keeps it correct. */
export function setSplitTarget(
  split: SplitDraft,
  targetId: string | undefined,
  targetsById: ReadonlyMap<string, Target>,
): void {
  split.targetId = targetId
  const target = targetId === undefined ? undefined : targetsById.get(targetId)
  if (target?.source === 'account') split.lastCategoryId = targetId
}

export function createRow(
  date: LocalDate,
  accountId: string | undefined,
  accountLedgerId: string | undefined = undefined,
): RowDraft {
  return {
    id: crypto.randomUUID(),
    date,
    accountId,
    accountLedgerId,
    memo: '',
    payee: undefined,
    tags: [],
    splits: [createSplit()],
  }
}

/** Resolves a chosen id to its {@link Target}, honouring which ledger it was picked
 * from: a foreign pick (`ledgerId` set) is never looked up in `targetsById, since a
 * fixture account id can coincide across ledgers (AGENTS.md) - it is matched by
 * `(id, ledgerId)` against the caller's own foreign-targets list instead. */
export function resolveTarget(
  id: string | undefined,
  ledgerId: string | undefined,
  targetsById: ReadonlyMap<string, Target>,
  foreignTargets: readonly Target[] = [],
): Target | undefined {
  if (id === undefined) return undefined
  return ledgerId === undefined
    ? targetsById.get(id)
    : foreignTargets.find((target) => target.id === id && target.ledgerId === ledgerId)
}

/** The row's own currency, once its primary account is chosen - every split's amount is
 * read in this currency, and a split whose own target is pinned to a different one is a
 * validation error rather than a silent conversion (AGENTS.md: no client-side FX). */
export function rowCurrency(
  row: RowDraft,
  targetsById: ReadonlyMap<string, Target>,
  foreignTargets: readonly Target[] = [],
): CurrencyCode | undefined {
  return resolveTarget(row.accountId, row.accountLedgerId, targetsById, foreignTargets)?.currency
}

export interface SplitProblem {
  readonly splitId: string
  readonly reason: 'target' | 'amount' | 'currency'
}

export function splitProblems(
  row: RowDraft,
  targetsById: ReadonlyMap<string, Target>,
): readonly SplitProblem[] {
  const currency = rowCurrency(row, targetsById)
  const problems: SplitProblem[] = []
  for (const split of row.splits) {
    if (split.targetId === undefined || split.targetId === row.accountId) {
      problems.push({ splitId: split.id, reason: 'target' })
      continue
    }
    if (split.amount === null) {
      problems.push({ splitId: split.id, reason: 'amount' })
      continue
    }
    const target = targetsById.get(split.targetId)
    if (target?.currency !== undefined && currency !== undefined && target.currency !== currency) {
      problems.push({ splitId: split.id, reason: 'currency' })
    }
  }
  return problems
}

/** Whether the row is complete enough to post: a date, a real money account with a
 * currency, at least one split, and every split free of a {@link SplitProblem}. */
export function isRowComplete(row: RowDraft, targetsById: ReadonlyMap<string, Target>): boolean {
  if (row.accountId === undefined) return false
  if (rowCurrency(row, targetsById) === undefined) return false
  if (row.splits.length === 0) return false
  return splitProblems(row, targetsById).length === 0
}

/** The row's own total: the sum of its splits' magnitudes, in the row's currency. Shown
 * on the row and folded into the per-account running delta - undefined while incomplete,
 * never a partially-summed guess. */
export function rowTotal(row: RowDraft, targetsById: ReadonlyMap<string, Target>): Money | undefined {
  const currency = rowCurrency(row, targetsById)
  if (currency === undefined || !isRowComplete(row, targetsById)) return undefined
  return row.splits.reduce((sum, split) => addMoney(sum, split.amount!), zeroMoney(currency))
}

/**
 * Builds the postings a {@link RowDraft} sends: the primary account carries the row's
 * total with the kind's own sign, each split carries the opposite sign. A transfer's
 * total is unsigned in the UI (AGENTS.md's kind-vs-sign rule) but still needs a sign on
 * the wire - the "to" split is always positive, the "from" account always negative,
 * matching the mock's own convention (`packages/testing/src/fixtures/entries.ts`).
 */
export function toPostEntryInput(
  kind: FlowKind,
  row: RowDraft,
  targetsById: ReadonlyMap<string, Target>,
): PostEntryInput | undefined {
  const currency = rowCurrency(row, targetsById)
  const total = rowTotal(row, targetsById)
  if (row.accountId === undefined || currency === undefined || total === undefined) return undefined

  const accountSign = kind === 'INCOME' ? 1 : -1
  const splitSign = -accountSign
  const accountAmount = accountSign === 1 ? total : negateMoney(total)
  const postings: PostingLineInput[] = [
    { accountId: row.accountId, amount: accountAmount },
    ...row.splits.map((split) => {
      const magnitude = split.amount!
      const target = targetsById.get(split.targetId!)
      return {
        accountId: split.targetId!,
        amount: splitSign === 1 ? magnitude : negateMoney(magnitude),
        ...(split.memo.trim() === '' ? {} : { memo: split.memo.trim() }),
        ...(target?.source === 'contact' && split.lastCategoryId !== undefined
          ? { splitFromAccountId: split.lastCategoryId }
          : {}),
      }
    }),
  ]

  return { ...entryFields(row), postings }
}

/** Everything a row sends besides its postings. */
export function entryFields(row: RowDraft): Omit<PostEntryInput, 'postings'> {
  return {
    date: row.date,
    ...(row.memo.trim() === '' ? {} : { memo: row.memo.trim() }),
    ...(row.payee === undefined || row.payee.trim() === '' ? {} : { payee: row.payee.trim() }),
    ...(row.tags.length === 0 ? {} : { tags: row.tags }),
  }
}

/**
 * The real change this row makes to its own money account - independent of which sign
 * the wire posting above carries for EXPENSE. That wire convention exists only to keep
 * `EntryResponse.total` matching what the transactions list already shows (the category
 * leg's own sign); a wallet's *running balance* has nothing to do with that and is always
 * "spending and sending reduce it, receiving increases it".
 */
export function primaryAccountDelta(
  kind: FlowKind,
  row: RowDraft,
  targetsById: ReadonlyMap<string, Target>,
): Money | undefined {
  const total = rowTotal(row, targetsById)
  if (total === undefined) return undefined
  return kind === 'INCOME' ? total : negateMoney(total)
}
